// "Run CV Analysis" for a product: asks for the subreddit hints before it
// starts. Prefilled with the product's saved hints; shows the brand-wide
// hints it inherits (set on the brand's form) read-only; saves any change
// on the product, then starts the analysis. Every product-level run entry
// point goes through here so nobody launches a Reddit scan blind — the
// hints are what make a niche product (r/vjing, not r/gadgets) findable.
import React, { useEffect, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, TextField } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import toast from 'react-hot-toast';
import axiosInstant from '../../api';
import { productConsumerVoiceEndpoint, productConsumerVoiceSettingsEndpoint } from '../../api/endpoints';
import { AnalysisScheduleFields, AnalysisSchedule } from '../Analysis/AnalysisScheduleFields';
import { scheduleLabel } from '../../utils/plans';

interface Props {
  desireid: number;
  // The product's manufacturer brand — the run is stamped with it.
  brandId: number;
  productTitle?: string | null;
  onClose: () => void;
  // The analysis was accepted (or was already running) — refetch the list.
  onStarted?: () => void;
}

export function ConsumerVoiceRunDialog({ desireid, brandId, productTitle, onClose, onStarted }: Props) {
  const [loading, setLoading] = useState(true);
  const [value, setValue] = useState('');
  const [saved, setSaved] = useState('');
  const [keywords, setKeywords] = useState('');
  const [savedKeywords, setSavedKeywords] = useState('');
  const [brand, setBrand] = useState<{ name: string | null; hints: string | null }>({ name: null, hints: null });
  // The product's schedule: how often Consumer Voice runs while a page of
  // the product is on Autopilot, and the day. Saved with the hints.
  const [schedule, setSchedule] = useState<AnalysisSchedule>({ interval: 'monthly', weekday: null, dayOfMonth: null });
  const [savedSchedule, setSavedSchedule] = useState<AnalysisSchedule>({ interval: 'monthly', weekday: null, dayOfMonth: null });
  const [maxInterval, setMaxInterval] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axiosInstant
      .get(`${productConsumerVoiceSettingsEndpoint(desireid)}?brandId=${brandId}`)
      .then((res) => {
        if (cancelled) return;
        const v = (res.data && res.data.cv_subreddits) || '';
        setValue(v);
        setSaved(v);
        const k = (res.data && res.data.cv_keywords) || '';
        setKeywords(k);
        setSavedKeywords(k);
        setBrand({
          name: (res.data && res.data.brand_name) || null,
          hints: (res.data && res.data.brand_cv_subreddits) || null,
        });
        const sched = {
          interval: (res.data && res.data.cv_analysis_interval) || 'monthly',
          weekday: res.data && res.data.cv_analysis_weekday !== undefined ? res.data.cv_analysis_weekday : null,
          dayOfMonth: res.data && res.data.cv_analysis_day_of_month !== undefined ? res.data.cv_analysis_day_of_month : null,
        };
        setSchedule(sched);
        setSavedSchedule(sched);
        setMaxInterval((res.data && res.data.max_interval) || null);
      })
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [desireid, brandId]);

  const run = async () => {
    setStarting(true);
    try {
      const scheduleChanged =
        schedule.interval !== savedSchedule.interval ||
        schedule.weekday !== savedSchedule.weekday ||
        schedule.dayOfMonth !== savedSchedule.dayOfMonth;
      if (value.trim() !== saved.trim() || keywords.trim() !== savedKeywords.trim() || scheduleChanged) {
        await axiosInstant.patch(`${productConsumerVoiceSettingsEndpoint(desireid)}?brandId=${brandId}`, {
          cv_subreddits: value,
          cv_keywords: keywords,
          cv_analysis_interval: schedule.interval,
          cv_analysis_weekday: schedule.weekday,
          cv_analysis_day_of_month: schedule.dayOfMonth,
        });
      }
      await axiosInstant.post(productConsumerVoiceEndpoint(desireid), { brandId });
      toast.success('Consumer Voice analysis started — the score updates when it finishes.');
      onStarted?.();
      onClose();
    } catch (e: any) {
      const status = e?.response?.status;
      if (status === 409) {
        toast('A Consumer Voice analysis is already running for this product.');
        onStarted?.();
        onClose();
      } else if (status === 429) {
        toast.error("You've reached the hourly limit for Consumer Voice analyses. Try again later.");
      } else if (e?.response?.data?.error === 'IntervalExceedsPlanMax') {
        toast.error("That schedule is faster than the brand's plan allows.");
      } else {
        toast.error("Couldn't start the Consumer Voice analysis. Please try again.");
      }
    } finally {
      setStarting(false);
    }
  };

  const noHints = !value.trim() && !(brand.hints || '').trim();

  return (
    <Dialog open onClose={starting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
        <span className="truncate">Run CV Analysis{productTitle ? ` — ${productTitle}` : ''}</span>
        <IconButton aria-label="Close" size="small" onClick={onClose} disabled={starting}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <p className="text-[13px] text-text-2 mb-3">
          Where do shoppers talk about this product on Reddit? The analysis searches these subreddits first,
          then all of Reddit for the brand and product name.
        </p>
        <TextField
          autoFocus
          fullWidth
          size="small"
          label="Subreddits to search first"
          placeholder="e.g. vjing, headphones, BudgetAudiophile"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          disabled={loading || starting}
          helperText="Comma-separated, with or without r/ (up to 12). Saved on the product for next time."
          sx={{ mt: 0.5 }}
        />
        {brand.hints ? (
          <p className="text-[12px] text-text-2 mt-2">
            Also inherited from {brand.name || 'the brand'}: <span className="font-semibold">{brand.hints}</span>
            <span className="text-muted"> (set on the brand&apos;s settings, applies to all its products)</span>
          </p>
        ) : (
          <p className="text-[12px] text-muted mt-2">
            {brand.name ? `${brand.name} has no brand-wide subreddits yet — ` : 'No brand-wide subreddits — '}
            add them on the brand&apos;s settings so every product inherits them.
          </p>
        )}
        <TextField
          fullWidth
          size="small"
          label="Search terms shoppers use"
          placeholder="e.g. soundlink flex, bose flex speaker"
          value={keywords}
          onChange={(e) => setKeywords(e.target.value)}
          disabled={loading || starting}
          helperText="Comma-separated (up to 10). Google Trends and Keyword Planner query these first; empty derives them from the product name."
          sx={{ mt: 2 }}
        />
        {noHints && !loading && (
          <p className="text-[12px] text-[#A0662E] dark:text-accent-text mt-2">
            No hints yet: the search falls back to defaults for the product&apos;s category, which miss niche
            communities. A subreddit or two makes a real difference.
          </p>
        )}
        <AnalysisScheduleFields
          label="Consumer Voice analysis interval"
          value={schedule}
          onChange={setSchedule}
          maxInterval={maxInterval}
          helperText={`Runs ${scheduleLabel(schedule.interval, schedule.weekday, schedule.dayOfMonth).toLowerCase()} while a page of this product is on Autopilot.`}
          disabled={loading || starting}
          className="mt-4"
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={starting}>
          Cancel
        </Button>
        <Button variant="contained" disableElevation onClick={run} disabled={loading || starting}>
          {starting ? 'Starting…' : 'Run analysis'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
